import { Box, Button, Grid } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useEffect, useRef, useState } from "react";
import AsyncSelect from "../../../../../components/AsyncSelect";
import AsyncSelectScroll from "@/components/AsyncSelectScroll";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../components/CustomSelect";
import CustomTextArea from "../../../../../components/CustomTextArea";
import CustomTimePicker from "../../../../../components/CustomTimePicker";
import ErrorHelper from "../../../../../components/ErrorWrapper";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import ProviderSelectScroll from "@/components/ProviderSelectScroll";
import StyledDialog from "../../../../../components/StyledDialog";
import Text from "../../../../../components/Text";
import TimeSlotPicker from "../../../../../components/TimeSlotPicker";
import ValueSwitcher from "../../../../../components/ValueSwitcher";
import { TIME_ZONE_WITH_OFFSET_LIST } from "../../../../../constants";
import {
  NEW_APPOINTMENT_INSURANCE_TYPE,
  NEW_APPOINTMENT_VISIT_TYPE,
} from "../../../../../constants/scheduling";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { useReduceDisclosure } from "../../../../../hooks/use-reduce-disclosure";
import { NewAppointmentType } from "../../../../../pages/app/provider/scheduling/appointment/appointment-constant";
import SelfPayDrawer from "../../../../../pages/app/provider/scheduling/availability/SelfPayDrawer";
import { useAppointmentTypeGetAll } from "../../../../../queries/practice-setting/appointment-type-configuration/use-appoinment-type-get-all";
import { getStripePromise } from "../../../../../queries/stripe/use-stripe";
import { useGetPatient } from "../../../../../queries/use-get-patient-uuid";
import { useLocationsList } from "../../../../../queries/use-locationList";
import { formatTime } from "../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
} from "../../../../../utils/utils";

interface NewPatientPortalAppointmentFormProps {
  formik: FormikProps<NewAppointmentType>;
}

export const NewPatientPortalAppointmentForm = ({
  formik,
}: NewPatientPortalAppointmentFormProps) => {
  const isTouched = useRef<boolean>(false);

  const [searchLocationKey, setSearchLocationKey] = useDebounce<string>("");
  const [locationPagination, setLocationPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: providerGroupLocationList, isFetching: locationFetching } =
    useLocationsList({
      pagination: locationPagination,
      searchKey: searchLocationKey,
      providerGroupId: getXtenantId(),
    });

  const { data: patientsData } = useGetPatient(formik.values.patientId?.value);

  useEffect(() => {
    const primary = patientsData?.patientInsurances?.filter(
      (ins) => ins.orderOfBenefits === "PRIMARY"
    );

    const secondary = patientsData?.patientInsurances?.filter(
      (ins) => ins.orderOfBenefits === "SECONDARY"
    );

    const patientTimezone = TIME_ZONE_WITH_OFFSET_LIST?.find(
      (it) => it.curType === patientsData?.timezone
    );

    formik.setFieldValue("timezone", patientTimezone);

    if (primary?.length)
      formik.setFieldValue("primary_insurance", { uuid: primary[0].uuid });
    if (secondary?.length)
      formik.setFieldValue("secondary_insurance", { uuid: secondary[0].uuid });
  }, [patientsData]);

  const [openDateCalendar, setOpenDateCalendar] = useState<boolean>(true);
  const [selectedDateTime, setSelectedDateTime] = useState<dayjs.Dayjs | null>(
    dayjs(new Date())
  );

  const [viewAvailabilityDisclosure, payDisc] = useReduceDisclosure(2);

  function toggleDateCalendar() {
    setOpenDateCalendar(!openDateCalendar);
    openDateCalendar && viewAvailabilityDisclosure.close();
  }

  const handleChangeMode = (mode: string) => {
    formik.setFieldValue("paymentType", mode as "CASH" | "INSURANCE");
    if (mode !== "INSURANCE") {
      payDisc.open({ uuid: null, patientId: patientsData?.uuid });
    }
  };

  const [searchAppointmentTypeKey, setSearchAppointmentTypeKey] =
    useDebounce<string>("");
  const [appointmentTypePagination, setAppointmentTypePagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const {
    data: appointmentTypeList,
    isLoading: appointmentTypeDataLoading,
    isFetching: appointmentTypeFetching,
  } = useAppointmentTypeGetAll({
    pagination: appointmentTypePagination,
    searchString: searchAppointmentTypeKey,
    status: true,
    archive: false,
    xTENANTID: getXtenantId(),
  });

  return (
    <>
      <Elements stripe={getStripePromise()}>
        <SelfPayDrawer
          disclosureHost={payDisc}
          shouldPassPatientId
          selectedMode={
            formik.values.paymentType !== "INSURANCE"
              ? formik.values.paymentType
              : undefined
          }
          setSelectedPaymentMethod={(mode: any) => {
            formik.setFieldValue("paymentType", mode as any);
          }}
        />
      </Elements>

      <Flex width={"100%"} flexDirection={"column"}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <AsyncSelectScroll
              required
              loading={appointmentTypeDataLoading || appointmentTypeFetching}
              error={formik.touched?.type && formik.errors?.type}
              name="type"
              handleBlur={formik.handleBlur}
              label="Appointment Type"
              placeholder="Select Appointment Type"
              onSearch={(val) => {
                setSearchAppointmentTypeKey(val);
                setAppointmentTypePagination((p) => ({ ...p, page: 0 }));
              }}
              pagination={appointmentTypePagination}
              setPagination={setAppointmentTypePagination}
              totalElements={appointmentTypeList?.data?.page?.totalElements}
              options={appointmentTypeList?.data?.content?.map((it: any) => ({
                value: it?.uuid,
                label: it?.nameString,
                duration: it?.duration,
              }))}
              value={
                formik.values.type && typeof formik.values.type === "object"
                  ? formik.values.type
                  : formik.values.type
                    ? {
                        value: formik.values.type,
                        label: formik.values.type,
                      }
                    : null
              }
              onChange={(value) => {
                formik.setFieldValue("type", value);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              textFieldProps={{
                placeholder: "Reason",
                name: "chiefComplaint",
                onBlur: formik.handleBlur,
                value: formik.values.chiefComplaint,
                onChange: (e) => {
                  formik.setFieldValue("chiefComplaint", e.currentTarget.value);
                },
              }}
              label="Reason for Visit"
              required
              error={
                formik.touched?.chiefComplaint &&
                (formik.errors?.chiefComplaint as any)
              }
            />
          </Grid>

          <Grid item xs={4}>
            <AsyncSelect
              required
              name="timezone"
              handleBlur={formik.handleBlur}
              error={
                formik.touched?.timezone && (formik.errors?.timezone as any)
              }
              label="Timezone"
              options={TIME_ZONE_WITH_OFFSET_LIST}
              placeholder="Select"
              value={formik.values?.timezone}
              onChange={(value) => {
                formik.setFieldValue("timezone", value);
              }}
            />
          </Grid>

          <Grid item xs={4}>
            <Flex alignItems={"center"} gap={1}>
              <Text>Visit Type:</Text>
              <ValueSwitcher
                alignment={formik.values.mode}
                handleChange={(_, value) => {
                  formik.setFieldValue(
                    "mode",
                    value as "IN_PERSON" | "VIRTUAL"
                  );
                  formik.setFieldValue("locationId", undefined);
                }}
                timing={NEW_APPOINTMENT_VISIT_TYPE}
              />
            </Flex>
          </Grid>
          <Grid item xs={4}>
            <AsyncSelectScroll
              label="Location"
              loading={locationFetching}
              options={providerGroupLocationList?.data?.content?.map((it) => ({
                value: it?.uuid,
                label: it?.name,
              }))}
              placeholder="Select"
              value={formik.values.locationId}
              onSearch={(val) => {
                setSearchLocationKey(val);
                setLocationPagination((p) => ({ ...p, page: 0 }));
              }}
              pagination={locationPagination}
              setPagination={setLocationPagination}
              totalElements={
                providerGroupLocationList?.data?.page?.totalElements
              }
              onChange={(value) => {
                if (!value) formik.setFieldValue("providerId", undefined);
                formik.setFieldValue("locationId", value);
              }}
              name="locationId"
              handleBlur={formik.handleBlur}
              error={formik.touched?.locationId && formik.errors?.locationId}
            />
          </Grid>
          <Grid item xs={9}>
            <ProviderSelectScroll
              required
              label="Provider"
              error={
                formik.touched.providerId && (formik.errors.providerId as any)
              }
              value={formik.values?.providerId || null}
              onChange={(value) => formik.setFieldValue("providerId", value)}
            />
          </Grid>
          <Grid item xs={3}>
            <ErrorHelper
              error={
                (isTouched.current || formik.submitCount > 0) &&
                (formik.errors?.duration as string)
              }
            >
              <Button
                disabled={!formik.values.providerId}
                sx={{
                  borderColor:
                    !formik.values?.duration && isTouched.current
                      ? "red"
                      : "primary.main",
                }}
                variant="outlined"
                fullWidth
                onClick={() => {
                  viewAvailabilityDisclosure.open();
                  setOpenDateCalendar(true);
                  isTouched.current = true;
                }}
              >
                View availability
              </Button>
            </ErrorHelper>
          </Grid>
          <Grid item xs={4}>
            <CustomDatePicker
              disabled
              onChange={() => {}}
              label="Date"
              value={formik.values?.duration ? selectedDateTime : undefined}
              // onChange={(value) => setSelectedDateTime(value)}
            />
          </Grid>
          <Grid item xs={4}>
            <CustomTimePicker
              valueFormatter="hh:mm A"
              disabled
              label="Time"
              value={
                formik?.values?.startTime && formik.values?.duration
                  ? formatTime(new Date(formik.values?.startTime), {
                      // withSeconds: true,
                      timezone: formik.values?.timezone?.value ?? undefined,
                    })
                  : undefined
              }
              // onChange={(value) => setTempTime(value)}
            />
          </Grid>

          <Grid item xs={4}>
            <CustomSelect
              readOnly
              label="Duration"
              value={formik.values?.duration}
              // onChange={(value) => setTempDuration(value)}
              options={
                formik.values?.duration
                  ? [
                      {
                        value: formik.values?.duration,
                        label: formik.values?.duration,
                      },
                    ]
                  : undefined
              }
            />
          </Grid>

          {/* In the Visit type field, if the user has selected 'In Person' visit type, then only this field will be displayed */}
          <StyledDialog
            open={viewAvailabilityDisclosure.opened}
            onClose={viewAvailabilityDisclosure.close}
          >
            <Grid item xs={12}>
              <Box width={"45rem"}>
                <TimeSlotPicker
                  isTouched={isTouched}
                  formik={formik}
                  appointmentType={
                    typeof formik.values.type === "object" &&
                    (formik.values.type as any)?.value
                      ? (formik.values.type as any).value
                      : (formik.values.type as any)
                  }
                  mode={formik.values.mode}
                  providerUuid={formik.values.providerId?.value ?? ""}
                  selectedLocation={
                    formik.values.mode === "VIRTUAL"
                      ? undefined
                      : formik.values.locationId?.value
                  }
                  // label="Date & Time"
                  onChange={(value) => {
                    setSelectedDateTime(value as dayjs.Dayjs);

                    if (
                      formik.values?.endDate &&
                      dayjs(formik?.values?.startTime).isAfter(
                        dayjs(formik?.values?.endDate)
                      )
                    ) {
                      formik.setFieldValue("endDate", null);
                    }
                  }}
                  selectedDateTime={selectedDateTime}
                  openDateCalendar={openDateCalendar}
                  toggleDateCalendar={toggleDateCalendar}
                  type="new"
                />
              </Box>
            </Grid>
          </StyledDialog>
          <Grid item container spacing={2}>
            <Grid item xs={12}>
              <Flex alignItems={"center"} gap={1}>
                <Text fontWeight={500}>Payment Mode:</Text>
                <ValueSwitcher
                  alignment={
                    formik.values?.paymentType !== "INSURANCE"
                      ? "ONLINE"
                      : formik.values?.paymentType
                  }
                  handleChange={(_, newAlignment) =>
                    handleChangeMode(newAlignment)
                  }
                  timing={NEW_APPOINTMENT_INSURANCE_TYPE}
                />
              </Flex>
            </Grid>
            {formik.values?.paymentType === "INSURANCE" && (
              <Grid item xs={6}>
                <CustomSelect
                  readOnly={!formik.values.patientId}
                  label="Primary Insurance"
                  options={patientsData?.patientInsurances?.map((fIns: any) => {
                    return {
                      value: fIns?.uuid,
                      label: fIns?.insurancePayer?.payerName,
                    };
                  })}
                  placeholder="Select Insurance"
                  value={formik.values.primary_insurance?.uuid}
                  onChange={(value) => {
                    const selectedIns =
                      patientsData?.patientInsurances?.findIndex(
                        (it: any) => it?.uuid === value
                      );

                    formik.setFieldValue(
                      "primary_insurance",
                      selectedIns !== undefined
                        ? {
                            uuid: patientsData?.patientInsurances?.[selectedIns]
                              ?.uuid,
                          }
                        : null
                    );
                  }}
                />
              </Grid>
            )}

            {formik.values?.paymentType === "INSURANCE" && (
              <Grid item xs={6}>
                <CustomSelect
                  readOnly={!formik.values.patientId}
                  label="Secondary Insurance"
                  options={patientsData?.patientInsurances?.map((fIns: any) => {
                    return {
                      value: fIns?.uuid,
                      label: fIns?.insurancePayer?.payerName,
                    };
                  })}
                  placeholder="Select Insurance"
                  value={formik.values.secondary_insurance?.uuid}
                  onChange={(value) => {
                    const selectedIns =
                      patientsData?.patientInsurances?.findIndex(
                        (it: any) => it?.uuid === value
                      );

                    formik.setFieldValue(
                      "secondary_insurance.uuid",
                      selectedIns !== undefined
                        ? patientsData?.patientInsurances?.[selectedIns]?.uuid
                        : null
                    );
                  }}
                  error={
                    // (formik.touched.secondary_insurance as any)?.uuid &&
                    (formik.errors.secondary_insurance as any)?.uuid
                  }
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <CustomTextArea
                minRows={3}
                label="Patient Appointment Note"
                placeholder="Appointment Note"
                onChange={formik.handleChange}
                name={"note"}
                value={formik.values.note}
              />
            </Grid>
          </Grid>
        </Grid>
      </Flex>
    </>
  );
};
